<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Device Configurations</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="4" sm="6">
                <company-select v-model="company" :items="companies" density="compact" label="Select Company"
                                @update:model-value="fetchConfigs"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th-btn :sorted-column="sortedColumn??null" column="id"
                                sortable title="ID" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="name"
                                sortable title="Name" @sort="sort"/>
                        <template v-if="user.type === 'admin'">
                            <th-btn :sorted-column="sortedColumn??null" column="company_name"
                                    sortable title="Company" @sort="sort"/>
                            <th-btn :sorted-column="sortedColumn??null" column="type"
                                    sortable title="Type" @sort="sort"/>
                        </template>
                        <th>Notification Recipients</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="config in configs"
                        v-if="configs.length && !loading" :key="config.id" class="text-left">
                        <td>{{ config.id }}</td>
                        <td>{{ config.name }}</td>
                        <template v-if="user.type === 'admin'">
                            <td>{{ config.company_name }}</td>
                            <td>{{
                                    config.type === 1 ? 'Default' : config.type === 3 ? 'System Alerts' : 'Non Default'
                                }}
                            </td>
                        </template>
                        <td>{{ config.notification_group }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editConfig(config)">Edit
                                </v-btn>
                                <v-btn v-if="config.type === 2" class="text-error" prepend-icon="mdi-delete"
                                       size="x-small"
                                       @click="deleteConfig(config)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="6">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchConfigs"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <Create-Config v-if="createDialogSwitch" v-model="createDialogSwitch" @addConfig="addConfig"/>
    <Edit-Config v-if="editDialogSwitch && selectedConfig.type !== 3" v-model="editDialogSwitch"
                 :config="selectedConfig" @updateConfig="updateConfig"/>
    <Edit-System-Alerts v-if="editDialogSwitch && selectedConfig.type === 3" v-model="editDialogSwitch"
                        :config="selectedConfig" @updateConfig="updateConfig"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import ThBtn from "@/components/globals/th-btn.vue";
import EditConfig from "@/components/dialogs/monitoring/configs/Edit.vue";
import CreateConfig from "@/components/dialogs/monitoring/configs/Create.vue";
import EditSystemAlerts from "@/components/dialogs/monitoring/configs/EditSystemAlerts.vue";
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore();
const company = ref((user.type !== 'admin') ? user.company.id : null);
const configs = ref([]);
const companies = ref([]);
const totalPages = ref(1);
const currentPage = ref(1);
const loading = ref(false);
const sortedType = ref(null);
const sortedColumn = ref(null);
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const selectedConfig = ref(null);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchConfigs();
    }
});
const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchConfigs();
}
const fetchConfigs = () => {
    loading.value = true;
    send('get', `monitoring/configs`, {
        page: currentPage.value,
        company: company.value,
        sort: sortedColumn.value,
        order: sortedType.value
    })
        .then((response) => {
            configs.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset alert configs could not be loaded', 'error')
        })
        .finally(() => loading.value = false);
}
const addConfig = (config) => {
    configs.value.unshift(config);
}
const editConfig = (config) => {
    selectedConfig.value = config;
    editDialogSwitch.value = true;
}
const updateConfig = (config) => {
    configs.value.map((conf, index) => {
        if (conf.id === config.id) {
            configs.value[index] = config;
        }
    });
}
const deleteConfig = (config) => {
    if (confirm(`Are you sure to delete selected config?`)) {
        send('delete', `monitoring/configs/${config.id}`)
            .then((response) => {
                if (response.data.success) {
                    configs.value.map((item, index) => {
                        if (item.id === config.id) {
                            configs.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${config.name}" config could not be deleted`, 'error')
                }
            })
    }
}
</script>
